import React, { useState, useEffect, useMemo } from 'react';
import { apiGet, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CalendarDays, Users, CreditCard, TrendingUp, 
  Clock, ArrowRight, Star, Dumbbell, Loader2, User, Phone, MapPin, Save, Ticket
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';

// Irish counties
const IRISH_COUNTIES = [
  'Antrim', 'Armagh', 'Carlow', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal',
  'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Kilkenny',
  'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath',
  'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone',
  'Waterford', 'Westmeath', 'Wexford', 'Wicklow'
];

// Eircode validation (Irish postal code format: A65 F4E2 or A65F4E2)
const validateEircode = (eircode) => {
  if (!eircode) return true;
  const pattern = /^[A-Za-z]\d{2}\s?[A-Za-z0-9]{4}$/;
  return pattern.test(eircode.trim());
};

// Mobile number validation (+353 8X XXXXXXX)
const validateMobileNumber = (mobile) => {
  if (!mobile) return true;
  const pattern = /^\+353\s?8[0-9]\s?[0-9]{3}\s?[0-9]{4}$/;
  return pattern.test(mobile.replace(/\s+/g, ' ').trim());
};

// Format mobile number as user types
const formatMobileNumber = (value) => {
  let digits = value.replace(/[^\d+]/g, '');
  
  if (!digits.startsWith('+353') && !digits.startsWith('+')) {
    if (digits.startsWith('353')) {
      digits = '+' + digits;
    } else if (digits.startsWith('08')) {
      digits = '+353' + digits.substring(1);
    } else if (digits.startsWith('8')) {
      digits = '+353' + digits;
    }
  }
  
  if (digits.startsWith('+353')) {
    const rest = digits.substring(4);
    if (rest.length <= 2) {
      return '+353 ' + rest;
    } else if (rest.length <= 5) {
      return '+353 ' + rest.substring(0, 2) + ' ' + rest.substring(2);
    } else {
      return '+353 ' + rest.substring(0, 2) + ' ' + rest.substring(2, 5) + ' ' + rest.substring(5, 9);
    }
  }
  
  return value;
};

export const ClientDashboard = () => {
  const { user, api, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [classPasses, setClassPasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Iter-46ai: Memoize the active-classpass list — used 3x in the same render
  // (length check + slice + map). Without memoization React would walk the
  // full classPasses array three times on every re-render.
  const activePasses = useMemo(
    () => classPasses.filter((p) => p.status === 'active'),
    [classPasses]
  );
  const activePassesTop3 = useMemo(() => activePasses.slice(0, 3), [activePasses]);
  
  // Profile edit state
  const [profileData, setProfileData] = useState({
    mobile: '',
    county: '',
    eircode: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Iter-46 bugfix: the dashboard tile is named "Active ClassPasses" — fetch
      // the user's ClassPasses (not subscriptions). Active = status active +
      // has remaining credits OR is unlimited.
      const [statsRes, bookingsRes, passesRes, profileRes] = await Promise.all([
        apiGet('/api/dashboard/stats'),
        apiGet('/api/bookings'),
        apiGet('/api/user-class-passes'),
        apiGet('/api/profile')
      ]);
      setStats(statsRes);
      // Iter-46k: filter the dashboard tile to truly upcoming bookings only —
      // status=confirmed AND class_start_time in the future. Previously the
      // tile mixed in cancelled rows and past classes since /api/bookings
      // returns the full history.
      const nowIso = new Date();
      const upcoming = (bookingsRes || [])
        .filter(b => b.status === 'confirmed')
        .filter(b => !b.class_start_time || new Date(b.class_start_time) >= nowIso)
        .sort((a, b) => new Date(a.class_start_time || 0) - new Date(b.class_start_time || 0));
      setBookings(upcoming.slice(0, 5));
      setClassPasses(passesRes || []);
      setProfileData({
        mobile: profileRes.phone || '',
        county: profileRes.county || '',
        eircode: profileRes.postal_code || ''
      });
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    const newErrors = {};
    if (profileData.mobile && !validateMobileNumber(profileData.mobile)) {
      newErrors.mobile = 'Please enter a valid Irish mobile number (+353 8X XXX XXXX)';
    }
    if (profileData.eircode && !validateEircode(profileData.eircode)) {
      newErrors.eircode = 'Please enter a valid Eircode (e.g., A65 F4E2)';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setProfileErrors(newErrors);
      toast.error('Please fix the validation errors');
      return;
    }
    
    setProfileErrors({});
    setProfileSaving(true);
    try {
      await apiPut('/api/profile', { body: {
        phone: profileData.mobile,
        county: profileData.county,
        postal_code: profileData.eircode
      } });
      toast.success('Profile updated!');
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update profile'));
    } finally {
      setProfileSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.first_name}!</h1>
          <p className="text-muted-foreground">Here's what's happening with your fitness journey.</p>
        </div>

        {/* Stats Grid — Iter-53b: 2-col on mobile so 3 stat cards fit as
            2+1 rather than a tall 1-col stack. */}
        <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-3">
          <Card data-testid="stat-bookings">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Classes Attended</CardTitle>
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-attended-count">{stats?.total_bookings || 0}</div>
              <p className="text-xs text-muted-foreground">
                {(stats?.upcoming_bookings || 0) > 0
                  ? `${stats.upcoming_bookings} upcoming`
                  : 'Past confirmed classes'}
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-subscriptions">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active ClassPasses</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.active_classpasses || 0}</div>
              <p className="text-xs text-muted-foreground">Active class passes</p>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-favorites">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Favourite Studios</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.favourite_studios || 0}</div>
              <p className="text-xs text-muted-foreground">Saved for quick access</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Upcoming Bookings */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Upcoming Classes</CardTitle>
                <CardDescription>Your scheduled sessions</CardDescription>
              </div>
              <Link to="/bookings">
                <Button variant="ghost" size="sm">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {bookings.length > 0 ? (
                <div className="space-y-4">
                  {bookings.map((booking, i) => (
                    <div 
                      key={booking.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      data-testid={`booking-item-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Dumbbell className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{booking.class_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {booking.class_start_time ? format(new Date(booking.class_start_time), 'EEE, MMM d • h:mm a') : 'Time TBD'}
                          </p>
                        </div>
                      </div>
                      <Badge variant={booking.status === 'confirmed' ? 'default' : 'secondary'}>
                        {booking.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground mb-4">No upcoming classes</p>
                  <Link to="/studios">
                    <Button variant="outline" size="sm" data-testid="browse-studios-btn">
                      Browse Studios
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active ClassPasses */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Active ClassPasses</CardTitle>
                <CardDescription>Your class passes</CardDescription>
              </div>
              <Link to="/memberships">
                <Button variant="ghost" size="sm">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {classPasses.filter(p => p.status === 'active').length > 0 ? (
                <div className="space-y-4">
                  {classPasses.filter(p => p.status === 'active').slice(0, 3).map((pass, i) => {
                    const remaining = pass.passes_remaining;
                    const total = pass.passes_total || 0;
                    const isUnlimited = remaining === -1 || pass.is_unlimited;
                    const progress = isUnlimited
                      ? 100
                      : total > 0
                        ? Math.max(0, Math.min(100, (remaining / total) * 100))
                        : 0;
                    let daysLeft = null;
                    // Bug R16 (Iter-65b): mirror the "never expires" check
                    // used on other surfaces so passes with a ~100-year
                    // expiry_date (validity_days=0 sentinel + 36500 days)
                    // don't render as "36500 days left" on the dashboard.
                    const neverExpires = pass.validity_days === 0
                      || pass.validity_days >= 1825
                      || (pass.expiry_date && (() => {
                        try {
                          const d = Math.ceil(
                            (new Date(pass.expiry_date) - new Date()) / (1000 * 60 * 60 * 24)
                          );
                          return d >= 1825;
                        } catch { return false; }
                      })());
                    if (pass.expiry_date && !neverExpires) {
                      try {
                        daysLeft = Math.max(
                          0,
                          Math.ceil((new Date(pass.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
                        );
                      } catch { /* ignore */ }
                    }

                    // Bug R28 fix (Iter-65): make the ClassPass card
                    // clickable so a logged-in client can jump straight
                    // to the studio's schedule and book. Previously the
                    // card was inert and the fallback nav dumped the user
                    // on home when the studio_slug happened to be null.
                    const linkTarget = pass.studio_slug
                      ? `/studio/${pass.studio_slug}?tab=schedule`
                      : '/schedule';
                    return (
                      <Link
                        key={pass.id}
                        to={linkTarget}
                        className="block p-4 rounded-lg border hover:border-primary hover:shadow-sm transition-all"
                        data-testid={`classpass-item-${i}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium">{pass.class_pass_name}</p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {formatActivity(pass.activity_type) || 'General'} • {isUnlimited ? 'Unlimited' : `${remaining} of ${total} remaining`}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                            Active
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{daysLeft !== null ? `${daysLeft} days left` : 'No expiry'}</span>
                            <span>{Math.round(progress)}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground mb-4">No active ClassPasses</p>
                  <Link to="/memberships">
                    <Button variant="outline" size="sm" data-testid="find-membership-btn">
                      Find a ClassPass
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/studios">
                <Button variant="outline" className="gap-2" data-testid="quick-browse-btn">
                  <Dumbbell className="w-4 h-4" />
                  Browse Studios
                </Button>
              </Link>
              <Link to="/memberships">
                <Button variant="outline" className="gap-2" data-testid="quick-classpasses-btn">
                  <Ticket className="w-4 h-4" />
                  ClassPasses
                </Button>
              </Link>
              <Link to="/schedule">
                <Button variant="outline" className="gap-2" data-testid="quick-schedule-btn">
                  <CalendarDays className="w-4 h-4" />
                  View Schedule
                </Button>
              </Link>
              <Link to="/bookings">
                <Button variant="outline" className="gap-2" data-testid="quick-bookings-btn">
                  <Clock className="w-4 h-4" />
                  My Bookings
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Profile Quick Edit */}
        <Card data-testid="profile-quick-edit">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Contact Details
              </CardTitle>
              <CardDescription>Keep your contact information up to date</CardDescription>
            </div>
            <Link to="/profile">
              <Button variant="ghost" size="sm">
                Full Profile
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Mobile Number
                </Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={profileData.mobile}
                  onChange={(e) => {
                    const formatted = formatMobileNumber(e.target.value);
                    setProfileData({ ...profileData, mobile: formatted });
                    if (profileErrors.mobile) setProfileErrors({ ...profileErrors, mobile: null });
                  }}
                  placeholder="+353 8X XXX XXXX"
                  className={profileErrors.mobile ? 'border-destructive' : ''}
                  data-testid="dashboard-mobile-input"
                />
                {profileErrors.mobile && (
                  <p className="text-xs text-destructive">{profileErrors.mobile}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="county" className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  County
                </Label>
                <Select 
                  value={profileData.county} 
                  onValueChange={(v) => setProfileData({ ...profileData, county: v })}
                >
                  <SelectTrigger data-testid="dashboard-county-select">
                    <SelectValue placeholder="Select county" />
                  </SelectTrigger>
                  <SelectContent>
                    {IRISH_COUNTIES.map((county) => (
                      <SelectItem key={county} value={county}>{county}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="eircode">Eircode</Label>
                <Input
                  id="eircode"
                  value={profileData.eircode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setProfileData({ ...profileData, eircode: value });
                    if (profileErrors.eircode) setProfileErrors({ ...profileErrors, eircode: null });
                  }}
                  placeholder="A65 F4E2"
                  className={profileErrors.eircode ? 'border-destructive' : ''}
                  maxLength={8}
                  data-testid="dashboard-eircode-input"
                />
                {profileErrors.eircode && (
                  <p className="text-xs text-destructive">{profileErrors.eircode}</p>
                )}
              </div>
            </div>
            <Button 
              onClick={handleSaveProfile} 
              disabled={profileSaving} 
              className="mt-4"
              data-testid="dashboard-save-profile-btn"
            >
              {profileSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Contact Details
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
